import { useRef, useState } from 'react'
import { Upload, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react'
import { uploadAudioAndProcess } from '../lib/uploadAudio'

interface Props {
  liverId: string
  onCallCreated?: (callId: string) => void
}

type State =
  | { kind: 'idle' }
  | { kind: 'uploading' }
  | { kind: 'processing'; callId: string }
  | { kind: 'success'; callId: string }
  | { kind: 'error'; message: string }

/**
 * Test/utility UI for uploading a pre-recorded audio file directly to
 * the call pipeline. Pre-Helper era: lets the user manually verify the
 * transcription + summary flow end-to-end.
 */
export function AudioUploadButton({ liverId, onCallCreated }: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [state, setState] = useState<State>({ kind: 'idle' })

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    e.target.value = ''
    setState({ kind: 'uploading' })
    try {
      const { callId } = await uploadAudioAndProcess({ liverId, file: f })
      setState({ kind: 'processing', callId })
      onCallCreated?.(callId)
      // Settle the UI back to idle after a moment; live status appears in
      // the calls list (subscribed via Realtime).
      setTimeout(() => setState({ kind: 'success', callId }), 1200)
      setTimeout(() => setState({ kind: 'idle' }), 6000)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setState({ kind: 'error', message: msg })
    }
  }

  function disabled() {
    return state.kind === 'uploading' || state.kind === 'processing'
  }

  function label() {
    switch (state.kind) {
      case 'uploading':
        return 'アップロード中…'
      case 'processing':
        return '文字起こし中…'
      case 'success':
        return '受付完了、議事録生成は Gateway で処理中'
      case 'error':
        return 'エラー'
      default:
        return '録音ファイルをアップロード'
    }
  }

  function Icon() {
    switch (state.kind) {
      case 'uploading':
      case 'processing':
        return <Loader2 className="w-4 h-4 animate-spin" />
      case 'success':
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />
      default:
        return <Upload className="w-4 h-4" />
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={disabled()}
        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm rounded-lg disabled:opacity-50"
      >
        <Icon />
        {label()}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={onFile}
      />
      {state.kind === 'error' && (
        <p className="text-xs text-red-600 leading-snug max-w-xs">{state.message}</p>
      )}
    </div>
  )
}
